import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { LAYERS } from './MapView';
import { svDest, svBearing, svDist } from '../lib/svgeo';
import { resolvePt, PIPE_TYPES } from '../lib/model';
import { fitColor } from './networkLayers';

const icon = (html, cls) => L.divIcon({ html, className: cls, iconSize: [0, 0] });
const cone = (cam, h, r) => [cam, ...[-45, -30, -15, 0, 15, 30, 45].map((a) => svDest(cam, h + a, r))];

// coupled 2D mini map: camera, view cone, draggable target, own objects
export default function useSvMiniMap(divRef, { cam, heading, dist, pipes, fittings, layout, setCam, setAim, onSel }) {
  const m = useRef({});
  const cb = useRef({});
  cb.current = { setCam, setAim, onSel, cam };
  useEffect(() => {
    const map = L.map(divRef.current, { zoomControl: false, attributionControl: false, maxZoom: 21 }).setView(cam, 19);
    const [url, maxZoom] = LAYERS.hybrid;
    L.tileLayer(url, { maxZoom, maxNativeZoom: 20 }).addTo(map);
    const objs = L.layerGroup().addTo(map);
    const coneL = L.polygon(cone(cam, heading, 25), { color: '#facc15', weight: 1.5, fillColor: '#facc15', fillOpacity: 0.18, interactive: false }).addTo(map);
    const ray = L.polyline([cam, cam], { color: '#ef4444', weight: 2.5, dashArray: '6 5', interactive: false }).addTo(map);
    const camM = L.marker(cam, { draggable: true, icon: icon('<span class="sv-cam" data-testid="sv-mini-cam">🚶</span>', 'sv-mk-wrap'), zIndexOffset: 900 }).addTo(map);
    const tgt = L.marker(cam, { draggable: true, icon: icon('<span class="sv-tgt" data-testid="sv-mini-target">🎯</span>', 'sv-mk-wrap'), zIndexOffset: 1000 }).addTo(map);
    const aim = (ll) => { const c = cb.current.cam; cb.current.setAim(svBearing(c, ll), svDist(c, ll)); };
    camM.on('dragend', () => { const p = camM.getLatLng(); cb.current.setCam([p.lat, p.lng]); });
    tgt.on('drag', () => { const p = tgt.getLatLng(); aim([p.lat, p.lng]); });
    map.on('click', (e) => aim([e.latlng.lat, e.latlng.lng]));
    m.current = { map, objs, coneL, ray, camM, tgt };
    return () => map.remove();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const { objs } = m.current;
    objs.clearLayers();
    pipes.forEach((pp) => {
      const pts = pp.points.map((x) => resolvePt(x, fittings)).filter(Boolean);
      if (pts.length > 1) objs.addLayer(L.polyline(pts, { color: pp.color || PIPE_TYPES[pp.type] || PIPE_TYPES.other, weight: 4, dashArray: pp.private ? '8 6' : null }).on('click', (e) => { L.DomEvent.stop(e); cb.current.onSel({ kind: 'pipe', id: pp.id }); }));
    });
    fittings.forEach((f) => objs.addLayer(L.circleMarker([f.lat, f.lon], { radius: 6, color: '#fff', weight: 1.5, fillColor: fitColor(f), fillOpacity: 1 }).on('click', (e) => { L.DomEvent.stop(e); cb.current.onSel({ kind: 'fitting', id: f.id }); })));
  }, [pipes, fittings]);
  useEffect(() => {
    const { coneL, ray, camM, tgt, map } = m.current;
    const tp = svDest(cam, heading, dist);
    coneL.setLatLngs(cone(cam, heading, 25));
    ray.setLatLngs([cam, tp]);
    camM.setLatLng(cam);
    tgt.setLatLng(tp);
    if (!map.getBounds().pad(-0.1).contains(tp)) map.panTo(tp);
  }, [cam, heading, dist]);
  useEffect(() => { const t = setTimeout(() => m.current.map.invalidateSize(), 260); return () => clearTimeout(t); }, [layout]);
}
